import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
  useImperativeHandle
} from 'react';
import { Button, PopoverProps } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import useConfig from '../../../Hooks/useConfig';
import fallback from '../../../../_Translations/generated/date-range-single-field.en.t.json';
import Keys from '../../../../_Translations/generated/date-range-single-field.en.t.json.keys';
import fallbackDateRange from '../../../../_Translations/generated/date-range-input.en.t.json';
import KeysDateRange from '../../../../_Translations/generated/date-range-input.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import { DateRangeDurationPropsType } from '../../../SubModules/PackageSearch/PackageSearch';
import { useTheme } from '@emotion/react';
import DateRangeInputSingleFieldPopover, {
  getFormattedDate
} from './DateRangeInputSingleFieldPopover';
import {
  dateToString,
  DefaultDate,
  durationToString,
  Mode
} from './DateRangeInputSingleFieldHelpers';
import useAppService from '../../../Hooks/useAppService';
import DateRangeDisplay from '../DateRangeInput/DateRangeDisplay';
export interface DateRangeInputSingleFieldRefType {
  focus?: (a: Mode) => void;
}
const DateRangeInputSingleField = forwardRef(
  (
    {
      onSubmit,
      startDate: startDateProp,
      endDate: endDateProp,
      popoverPlacement,
      minDate,
      maxDate,
      disabled,
      defaultDate,
      onToggle,
      duration: durationProp,
      useDateDisplay,
      alternativeDateLabels,
      minDateRangeSpan,
      maxDateRangeSpan,
      label
    }: {
      onSubmit: (dates: [string | undefined, string | undefined], duration?: number) => void;
      startDate?: string | null;
      endDate?: string | null;
      popoverPlacement?: PopoverProps['placement'];
      minDate?: string;
      maxDate?: string;
      disabled?: boolean;
      defaultDate?: DefaultDate;
      onToggle?: (state: { isOpen?: boolean; startDate?: Date; endDate?: Date }) => void;
      duration?: DateRangeDurationPropsType;
      useDateDisplay?: boolean;
      alternativeDateLabels?: {
        startDate: string;
        endDate: string;
        duration: string;
      };
      minDateRangeSpan?: number;
      maxDateRangeSpan?: number;
      label?: string;
    },
    ref?: ForwardedRef<DateRangeInputSingleFieldRefType>
  ): JSX.Element => {
    const config = useConfig();
    const theme = useTheme();
    const appService = useAppService();
    const { t } = useTranslation('date-range-single-field', fallback, Keys);
    const { t: tDateRange } = useTranslation('date-range-input', fallbackDateRange, KeysDateRange);
    const duration = durationProp?.value?.duration;

    const [mode, setMode] = useState<Mode>('none');
    const [startDate, setStartDate] = useState<string | undefined>(
      defaultDate?.startDate
        ? getFormattedDate(config, appService, defaultDate.startDate)
        : undefined
    );
    const [endDate, setEndDate] = useState<string | undefined>(
      defaultDate?.endDate ? getFormattedDate(config, appService, defaultDate.endDate) : undefined
    );
    const [localDuration, setLocalDuration] = useState<number | undefined>(
      durationProp?.value?.duration
    );

    const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
    const oldStartDatePropRef = useRef<string | undefined>(undefined);
    const oldEndDatePropRef = useRef<string | undefined>(undefined);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      focus: (a: Mode) => {
        handleInputClick(a);
      }
    }));

    useEffect(() => {
      if (!!duration) {
        setLocalDuration(duration);
      }
    }, [duration]);

    useEffect(() => {
      if (!!startDateProp && startDateProp !== oldStartDatePropRef.current) {
        setStartDate(dayjs(startDateProp).format(config.displayFormatDate[appService.lang]));
        oldStartDatePropRef.current = startDateProp;
      }
      if (!!endDateProp && endDateProp !== oldEndDatePropRef.current) {
        setEndDate(dayjs(endDateProp).format(config.displayFormatDate[appService.lang]));
        oldEndDatePropRef.current = endDateProp;
      }
    }, [startDateProp, endDateProp]);

    useEffect(() => {
      if (popoverOpen) {
        if (!startDateProp && !defaultDate?.startDate) {
          setStartDate(undefined);
        }
        if (!endDateProp && !defaultDate?.startDate) {
          setEndDate(undefined);
        }
      }
    }, [popoverOpen]);

    useEffect(() => {
      if (mode === 'selectsStart' || mode === 'selectsEnd') {
      }
    }, [mode]);

    const handleInputClick = useCallback(
      (inputMode: Mode): void => {
        setMode(inputMode);
      },
      [mode]
    );
    const displayFormat = config.displayFormatDate[appService.lang];

    const togglePopover = useCallback((): void => {
      if (!disabled) {
        if (
          onToggle &&
          dayjs(endDate, displayFormat).isValid() &&
          dayjs(startDate, displayFormat).isValid()
        ) {
          onToggle({
            endDate: dayjs(endDate, displayFormat).toDate(),
            startDate: dayjs(startDate, displayFormat).toDate(),
            isOpen: !popoverOpen
          });
        }
        setPopoverOpen(value => !value);
      }
    }, [popoverOpen, endDate, startDate]);

    const handleSubmit = useCallback(
      (validStartDate: string, validEndDate: string): void => {
        onSubmit(
          [
            dayjs(validStartDate, config.displayFormatDate[appService.lang]).toISOString(),
            dayjs(validEndDate, config.displayFormatDate[appService.lang]).toISOString()
          ],
          localDuration
        );
        setPopoverOpen(false);
        setStartDate(validStartDate);
        setEndDate(validEndDate);
      },
      [onSubmit, config, localDuration]
    );

    return (
      <div ref={containerRef} className="date-range-single">
        {useDateDisplay ? (
          <>
            {!!label && <label>{label}</label>}
            <DateRangeDisplay
              firstDate={!!startDateProp ? dateToString(startDateProp, config) : ''}
              secondDate={!!endDateProp ? dateToString(endDateProp, config) : ''}
              displayIcon={theme.useCalenderIcon}
              fromTranslation={tDateRange(KeysDateRange.from)}
              untilTranslation={tDateRange(KeysDateRange.to)}
              dataTestId={'dateRangeInputSingleFildDateDisplay'}
              displayDuration={durationProp?.withDuration}
              duration={durationToString(tDateRange, duration)}
              innerRef={buttonRef}
              disabled={disabled}
              onClick={togglePopover}
            />
          </>
        ) : (
          <Button
            className="date-range-single__button w-100"
            color="primary"
            innerRef={buttonRef}
            onClick={togglePopover}
            disabled={disabled}
          >
            <span>{t(Keys.travelPeriod)}</span>
            <FontAwesomeIcon icon={faAngleDown} />
          </Button>
        )}
        <DateRangeInputSingleFieldPopover
          containerRef={containerRef}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          mode={mode}
          setMode={setMode}
          setLocalDuration={setLocalDuration}
          buttonRef={buttonRef}
          handleInputClick={handleInputClick}
          popoverOpen={popoverOpen}
          setPopoverOpen={setPopoverOpen}
          togglePopover={togglePopover}
          handleSubmit={handleSubmit}
          startDate={startDate}
          endDate={endDate}
          startDateProp={startDateProp}
          endDateProp={endDateProp}
          defaultDate={defaultDate}
          minDate={minDate}
          maxDate={maxDate}
          localDuration={localDuration}
          popoverPlacement={popoverPlacement}
          disabled={disabled}
          durationProp={durationProp}
          alternativeDateLabels={alternativeDateLabels}
          minDateRangeSpan={minDateRangeSpan}
          maxDateRangeSpan={maxDateRangeSpan}
        />
      </div>
    );
  }
);

export default DateRangeInputSingleField;
